/**
 * Typed API client (U15) — thin fetch wrappers over the public endpoints,
 * with every response parsed through the SHARED zod schemas (the single
 * contract source; drift fails loudly, never silently).
 *
 * Same-origin by construction: dev Vite proxies `/api` → 4200; in production
 * the API serves this app itself.
 */
import {
  PublicAcceptResponseSchema,
  PublicConsentResponseSchema,
  PublicDeclineResponseSchema,
  PublicInviteClaimResponseSchema,
  PublicInviteResponseSchema,
  PublicProposalResponseSchema,
  RenderModelSchema,
} from '@buildledger/shared';
import type {
  PublicAcceptRequest,
  PublicAcceptResponse,
  PublicConsentResponse,
  PublicDeclineRequest,
  PublicDeclineResponse,
  PublicInviteClaimRequest,
  PublicInviteClaimResponse,
  PublicInviteResponse,
  PublicProposalResponse,
  RenderModel,
} from '@buildledger/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError(0, 'Could not reach the server — check your connection and try again.');
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? Array.isArray((body as { message: unknown }).message)
          ? ((body as { message: string[] }).message[0] ?? res.statusText)
          : String((body as { message: unknown }).message)
        : res.statusText;
    throw new ApiError(res.status, message);
  }
  return body;
}

// ---------------------------------------------------------------- proposals

export function getProposal(token: string): Promise<PublicProposalResponse> {
  return request(`/public/proposals/${token}`).then((body) =>
    PublicProposalResponseSchema.parse(body),
  );
}

export function postConsent(token: string): Promise<PublicConsentResponse> {
  return request(`/public/proposals/${token}/consent`, {
    method: 'POST',
    body: JSON.stringify({ agree: true }),
  }).then((body) => PublicConsentResponseSchema.parse(body));
}

export function postAccept(
  token: string,
  dto: PublicAcceptRequest,
): Promise<PublicAcceptResponse> {
  return request(`/public/proposals/${token}/accept`, {
    method: 'POST',
    body: JSON.stringify(dto),
  }).then((body) => PublicAcceptResponseSchema.parse(body));
}

export function postDecline(
  token: string,
  dto: PublicDeclineRequest,
): Promise<PublicDeclineResponse> {
  return request(`/public/proposals/${token}/decline`, {
    method: 'POST',
    body: JSON.stringify(dto),
  }).then((body) => PublicDeclineResponseSchema.parse(body));
}

export function proposalPdfUrl(token: string): string {
  return `/api/v1/public/proposals/${token}/pdf`;
}

export function signedCopyUrl(token: string): string {
  return `/api/v1/public/proposals/${token}/signed-copy.pdf`;
}

// ------------------------------------------------------------------ invites

export function getInvite(token: string): Promise<PublicInviteResponse> {
  return request(`/public/invites/${token}`).then((body) =>
    PublicInviteResponseSchema.parse(body),
  );
}

export function postClaim(
  token: string,
  dto: PublicInviteClaimRequest,
): Promise<PublicInviteClaimResponse> {
  return request(`/public/invites/${token}/claim`, {
    method: 'POST',
    body: JSON.stringify(dto),
  }).then((body) => PublicInviteClaimResponseSchema.parse(body));
}

// ------------------------------------------------------------ print session

/** What PdfService (U14) hands the /print route via its one-time session. */
export interface PrintSessionPayload {
  document: RenderModel;
  certificate: PrintCertificate | null;
  logoDataUri: string | null;
}

/** Signature-certificate attribution block (shape owned by U14's seam). */
export interface PrintCertificate {
  signerName: string;
  signerEmail: string | null;
  typedSignature: string | null;
  ip: string | null;
  userAgent: string | null;
  consentTextSnapshot: string | null;
  consentGivenAt: string | null;
  intentConfirmedAt: string | null;
  signedAt: string | null;
  documentHash: string;
  renderContentHash: string;
}

/**
 * Print sessions are ONE-TIME: the first read consumes them server-side.
 * React 18 StrictMode double-invokes effects in dev, so the fetch is memoized
 * per session token — both invocations share one request.
 */
const printSessionCache = new Map<string, Promise<PrintSessionPayload>>();

export function getPrintSession(session: string): Promise<PrintSessionPayload> {
  const cached = printSessionCache.get(session);
  if (cached) return cached;
  const promise = request(`/pdf/print-sessions/${session}`).then((raw) => {
    const body = raw as {
      document: unknown;
      certificate: PrintCertificate | null;
      logoDataUri: string | null;
    };
    return {
      document: RenderModelSchema.parse(body.document),
      certificate: body.certificate ?? null,
      logoDataUri: body.logoDataUri ?? null,
    };
  });
  printSessionCache.set(session, promise);
  promise.catch(() => printSessionCache.delete(session));
  return promise;
}
